/**
 * Finish-recording lifecycle FSM.
 *
 * Owns `handleFinish`: stops the MediaRecorder + WS, flushes any remaining
 * chunks, optionally calls REST stopStreaming, then polls
 * /api/meetings/:id every 2s up to 240s waiting for status="summarized" or
 * "error" before transitioning back to idle.
 *
 * Two paths:
 *   1. Streaming mode — server is doing the heavy lifting, so we send a
 *      WS "stop", upload trailing chunks, REST-call stopStreaming, then
 *      poll for completion.
 *   2. Batch mode — wait for any in-flight chunk upload to drain (up to
 *      300s), then do a final upload, then poll for completion. If the
 *      final upload fails and we're offline / provisional we save back
 *      to IDB and let the recovery path pick it up next visit.
 */
import { MEETING_STATUSES, type TStreamControlMessage } from "@lexicanon/shared"
import type { I18n } from "@lingui/core"
import { msg } from "@lingui/core/macro"
import { useCallback, useRef } from "react"
import type { CompanyApi } from "../../../hooks/useCompanyApi"
import { clearChunksFromIDB, saveChunksToIDB } from "../../../hooks/useIndexedDB"
import { fetchMeeting, stopStreaming } from "../../../lib/api"
import type { TProcessingStage } from "../../FinalizationProgress"
import type { IRemoteUploadHandle, TRecordingMode, TRecordingState } from "../interfaces"

interface IProps {
  meetingId: string
  subject?: string
  templateId?: string
  mode: TRecordingMode
  company: CompanyApi | null
  isOnlineRef: React.MutableRefObject<boolean>
  provisionalPendingRef: React.MutableRefObject<boolean>
  mediaRecorderRef: React.MutableRefObject<MediaRecorder | null>
  wsRef: React.MutableRefObject<WebSocket | null>
  chunksRef: React.MutableRefObject<Blob[]>
  uploadBatchIndexRef: React.MutableRefObject<number>
  absoluteBaseRef: React.MutableRefObject<number>
  uploadingRef: React.MutableRefObject<boolean>
  startTimeRef: React.MutableRefObject<number>
  heartbeatIntervalRef: React.MutableRefObject<ReturnType<typeof setInterval> | null>
  doChunkUpload: (isFinal: boolean) => Promise<boolean>
  stopChunkUploadTimer: () => void
  stopElapsedTimer: () => void
  stopPeriodicSave: () => void
  releaseWakeLock: () => void
  handleStreamingStopped: () => void
  setShowFinishConfirm: (open: boolean) => void
  setState: (state: TRecordingState) => void
  setRecordingActive: (active: boolean) => void
  setUploadCount: React.Dispatch<React.SetStateAction<number>>
  setError: (err: string) => void
  setProcessingStage: (stage: TProcessingStage | null) => void
  onRecordingStopped?: () => void
  /**
   * Called when the finalize poll is exhausted but the server is still working —
   * the host keeps the finalize surface mounted with a "taking longer than usual"
   * notice instead of dropping silently to idle (which looked identical to
   * success). When omitted, the legacy behaviour (idle + onRecordingStopped)
   * applies so non-Lexicanon callers are unchanged.
   */
  onFinalizeTimeout?: () => void
  /**
   * Desktop hybrid far-end channel. When present, its complete file is pushed
   * (or held in IDB when offline) BEFORE the room final triggers processing.
   * `undefined` on web ⇒ room-only finish, byte-for-byte unchanged.
   */
  remoteUpload?: IRemoteUploadHandle
  i18n: I18n
}

interface IUseFinishLifecycleResult {
  handleFinish: () => Promise<void>
}

/** Cap on the far-end final-drain upload so a degraded link can't hang the finish UI. */
const REMOTE_FINAL_DRAIN_TIMEOUT_MS = 8000

export function useFinishLifecycle(props: IProps): IUseFinishLifecycleResult {
  const {
    meetingId,
    subject,
    templateId,
    mode,
    company,
    isOnlineRef,
    provisionalPendingRef,
    mediaRecorderRef,
    wsRef,
    chunksRef,
    uploadBatchIndexRef,
    absoluteBaseRef,
    uploadingRef,
    startTimeRef,
    heartbeatIntervalRef,
    doChunkUpload,
    stopChunkUploadTimer,
    stopElapsedTimer,
    stopPeriodicSave,
    releaseWakeLock,
    handleStreamingStopped,
    setShowFinishConfirm,
    setState,
    setRecordingActive,
    setUploadCount,
    setError,
    onRecordingStopped,
    onFinalizeTimeout,
    remoteUpload,
    i18n,
  } = props

  // Drain the far-end ("remote") channel to a COMPLETE file before the room
  // final kicks off processing. A remote `isFinal` only closes
  // `{id}.remote.webm`; the room final is the single processing trigger and
  // reads that file (the backend `waitForRemoteFile` / streaming-stop wait are
  // the safety nets). Offline / provisional ⇒ hold the far-end in IDB for
  // recovery. No-op on web (no remote channel).
  // biome-ignore lint/correctness/useExhaustiveDependencies: stable refs/handle
  const drainRemoteFinal = useCallback(async (): Promise<void> => {
    if (!remoteUpload) return
    const { stopRemote, chunksRef: remoteChunksRef, doChunkUpload: remoteDoChunkUpload } = remoteUpload
    await stopRemote().catch(() => {})
    // Let the MediaRecorder's final `ondataavailable` flush into the buffer.
    await new Promise((r) => setTimeout(r, 300))
    if (remoteChunksRef.current.length === 0) return
    // Bound the far-end final upload — on a degraded-but-online link it could
    // otherwise neither resolve nor reject, parking the finish UI in
    // "finalizing" forever (the backend `waitForRemoteFile` net runs server-side
    // *after* this await and can't unstick the client). On timeout we treat it as
    // not-uploaded and fall through to the IDB-hold + room finalize.
    const uploaded = await Promise.race([
      remoteDoChunkUpload(true),
      new Promise<boolean>((resolve) => setTimeout(() => resolve(false), REMOTE_FINAL_DRAIN_TIMEOUT_MS)),
    ])
    // Gold copy: even a fully-uploaded far-end stays in IDB until the meeting
    // is SUMMARIZED (cleared in the finalize poll below) — never before.
    if (!uploaded && (!isOnlineRef.current || provisionalPendingRef.current)) {
      await saveChunksToIDB(
        meetingId,
        remoteChunksRef.current,
        startTimeRef.current,
        remoteUpload.absoluteBaseRef.current,
        {
          isProvisional: provisionalPendingRef.current,
          subject,
          templateId,
          channel: "remote",
          offsetSec: remoteUpload.offsetSecRef.current,
          uploadBatchIndex: remoteUpload.uploadBatchIndexRef.current,
        },
      ).catch(() => {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable refs (see biome-ignore)
  }, [remoteUpload, meetingId, subject, templateId])

  // Re-entry guard — finish is async (drain → stop → upload → poll). A second
  // click would otherwise fire a duplicate stop/upload/poll. See §1.5.
  const pendingRef = useRef(false)

  // biome-ignore lint/correctness/useExhaustiveDependencies: many deps are stable refs
  const runFinish = useCallback(async (): Promise<void> => {
    if (!company) return
    setShowFinishConfirm(false)
    setState("finalizing")
    // Keep the recording guard up through the last-chunk upload (or IDB
    // persist). Clearing it here used to let a waiting PWA update — or a
    // vite:preloadError reload — kill the tab while trailing audio was still
    // only in memory. The SUMMARIZED poll below is watch-only and releases
    // the guard first.
    sessionStorage.removeItem("lexicanon:activeRecording")
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current)
      heartbeatIntervalRef.current = null
    }
    stopChunkUploadTimer()
    stopElapsedTimer()
    stopPeriodicSave()
    releaseWakeLock()
    handleStreamingStopped()
    mediaRecorderRef.current?.stop()
    await new Promise((r) => setTimeout(r, 500))

    // Hybrid desktop: finish the far-end file BEFORE the room path below triggers
    // (streaming-stop or room-final) processing, so the merge/mix sees a complete
    // {id}.remote.webm. No-op on web / mic-only.
    await drainRemoteFinal()

    if (mode === "streaming") {
      try {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          const stopMsg: TStreamControlMessage = { type: "stop" }
          wsRef.current.send(JSON.stringify(stopMsg))
          // Let the stop frame flush before close. WKWebView drops a send()
          // that is immediately followed by close(). The server also treats
          // close(1000, "stop") as intentional, so a dropped stop frame no
          // longer looks like a crash.
          await new Promise((r) => setTimeout(r, 300))
        }
        wsRef.current?.close(1000, "stop")
        wsRef.current = null
      } catch {
        /* ignore WS close errors */
      }

      if (chunksRef.current.length > 0) {
        const uploaded = await doChunkUpload(true)
        // Uploaded chunks stay in IDB (gold copy) until SUMMARIZED — see the poll below.
        if (!uploaded && (!isOnlineRef.current || provisionalPendingRef.current)) {
          // Finishing offline in streaming mode: the trailing chunk(s) appended
          // after the last periodic snapshot would otherwise be lost — the
          // periodic IDB saver is already stopped above, and there is no else
          // branch to re-persist them (unlike the batch path). Hold them for
          // recovery and skip the finalize poll (no server will summarize an
          // offline meeting). Never-lose-a-meeting invariant.
          await saveChunksToIDB(meetingId, chunksRef.current, startTimeRef.current, absoluteBaseRef.current, {
            isProvisional: provisionalPendingRef.current,
            subject,
            templateId,
            uploadBatchIndex: uploadBatchIndexRef.current,
          }).catch(() => {})
          setState("idle")
          setRecordingActive(false)
          onRecordingStopped?.()
          return
        }
      }

      // Last capture bytes are on the server (or there were none). Polling
      // for SUMMARIZED does not need the guard — a reload is data-safe.
      setRecordingActive(false)

      try {
        await stopStreaming(company, meetingId)
      } catch {
        /* session may already be stopped via WS */
      }

      for (let i = 0; i < 120; i++) {
        await new Promise((r) => setTimeout(r, 2000))
        try {
          const data = (await fetchMeeting(company, meetingId)) as { status: string }
          if (data) {
            if (data.status === MEETING_STATUSES.SUMMARIZED) {
              // Server confirmed the meeting is fully processed — the local gold
              // copy has served its purpose.
              await clearChunksFromIDB(meetingId)
              setState("idle")
              onRecordingStopped?.()
              return
            }
            if (data.status === MEETING_STATUSES.ERROR) {
              setError(i18n._(msg`Finalization failed.`))
              setState("idle")
              onRecordingStopped?.()
              return
            }
          }
        } catch {
          /* polling — ignore */
        }
      }
      // Poll exhausted while the server is still processing. Don't masquerade as
      // success — keep the finalize surface up with a "taking longer" notice when
      // the host supports it, else fall back to the legacy idle behaviour.
      if (onFinalizeTimeout) {
        onFinalizeTimeout()
        return
      }
      setState("idle")
      onRecordingStopped?.()
      return
    }

    if (uploadingRef.current) {
      for (let i = 0; i < 300 && uploadingRef.current; i++) await new Promise((r) => setTimeout(r, 1000))
    }

    if (chunksRef.current.length > 0) {
      const uploaded = await doChunkUpload(true)
      if (uploaded) {
        // IDB gold copy is cleared only once the poll below sees SUMMARIZED.
        setUploadCount((c) => c + 1)
        setError("")
      } else {
        if (!isOnlineRef.current || provisionalPendingRef.current) {
          if (chunksRef.current.length > 0) {
            await saveChunksToIDB(meetingId, chunksRef.current, startTimeRef.current, absoluteBaseRef.current, {
              isProvisional: provisionalPendingRef.current,
              subject,
              templateId,
              uploadBatchIndex: uploadBatchIndexRef.current,
            }).catch(() => {})
          }
          setState("idle")
          setRecordingActive(false)
          onRecordingStopped?.()
          return
        }
        setError(i18n._(msg`Final upload failed. Recording is saved locally and will retry on next visit.`))
        setState("paused")
        // Guard stays up — trailing audio is still only in memory / IDB and
        // the session is paused, not finished. A reload would drop it.
        return
      }
    }

    setRecordingActive(false)

    for (let i = 0; i < 120; i++) {
      await new Promise((r) => setTimeout(r, 2000))
      try {
        const data = (await fetchMeeting(company, meetingId)) as { status: string }
        if (data) {
          if (data.status === MEETING_STATUSES.SUMMARIZED) {
            // Server confirmed the meeting is fully processed — the local gold
            // copy has served its purpose.
            await clearChunksFromIDB(meetingId)
            setState("idle")
            onRecordingStopped?.()
            return
          }
          if (data.status === MEETING_STATUSES.ERROR) {
            setError(i18n._(msg`Finalization failed.`))
            setState("idle")
            onRecordingStopped?.()
            return
          }
        }
      } catch {
        /* polling — ignore */
      }
    }
    // Poll exhausted, server still processing — keep the finalize surface with a
    // "taking longer" notice rather than silently going idle (which read as done).
    if (onFinalizeTimeout) {
      onFinalizeTimeout()
      return
    }
    setState("idle")
    onRecordingStopped?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- many deps are stable refs (see biome-ignore on declaration)
  }, [
    meetingId,
    subject,
    templateId,
    onRecordingStopped,
    stopChunkUploadTimer,
    stopElapsedTimer,
    stopPeriodicSave,
    releaseWakeLock,
    handleStreamingStopped,
    mode,
    company,
    doChunkUpload,
    drainRemoteFinal,
    setRecordingActive,
    onFinalizeTimeout,
  ])

  const handleFinish = useCallback(async (): Promise<void> => {
    if (pendingRef.current) return
    pendingRef.current = true
    try {
      await runFinish()
    } finally {
      pendingRef.current = false
    }
  }, [runFinish])

  return { handleFinish }
}
